import type { ServicePageData } from './types';

const pvcBanners: ServicePageData = {
  slug: 'pvc-banners',
  eyebrow: 'Signage',
  title: 'Get your message up fast with',
  titleAccent: 'printed PVC banners.',
  intro:
    'Hemmed, eyeleted and ready to hang the same day it leaves our print room. Whether it is a fence line at a building site or a backdrop for a weekend event, we print, finish and fit banners to whatever size you need.',
  heroImage: '/home/signboards/signboards-banners.jpg',

  useCases: [
    { title: 'Fences and hoarding', text: 'Wrap a site boundary with eyeletted banners cable-tied along the top and bottom.' },
    { title: 'Scaffolding', text: 'Turn a scaffold wrap into advertising space while the building work is underway.' },
    { title: 'Shop fronts', text: 'A quick, affordable way to announce a sale, refit or new opening.' },
    { title: 'Events and exhibitions', text: 'Backdrops, entrance banners and stage dressing that pack flat and travel well.' },
    { title: 'Walls and railings', text: 'Eyelets and ties fixed wherever you need them, indoors or out.' },
    { title: 'Windy or exposed sites', text: 'Mesh banners let the wind through instead of fighting it.' },
  ],

  optionsHeading: 'Choose your banner',
  optionsIntro: 'All banners are hemmed on every edge with eyelets set to your spacing, ready to hang.',
  options: [
    {
      id: 'standard-pvc',
      title: 'Standard PVC',
      lead: 'Our most popular banner for short to medium-term use.',
      body: 'A durable printed PVC banner suited to most outdoor and indoor jobs. Full-colour print, hemmed edges and eyelets as standard, sized to exactly what you need.',
      image: '/home/signboards/signboards-banners.jpg',
      specs: [
        { label: 'Size', value: 'Any size up to our maximum roll width, banners over that are seamed.' },
        { label: 'Material', value: 'Printed PVC banner material, matt finish.' },
        { label: 'Finish', value: 'Hemmed edges with eyelets set to your spacing.' },
        { label: 'Fixings', value: 'Cable ties or rope through the eyelets, not supplied.' },
      ],
    },
    {
      id: 'heavy-duty-pvc',
      title: 'Heavy-duty PVC',
      lead: 'A heavier weight banner for long-term outdoor use.',
      body: 'The same print quality on a thicker, more rigid PVC that copes better with repeated wind and weather over a longer run outdoors, such as a site hoarding left up for months.',
      image: '/home/signboards/signboards-walkers.jpg',
      specs: [
        { label: 'Size', value: 'Any size up to our maximum roll width, banners over that are seamed.' },
        { label: 'Material', value: 'Heavier-weight printed PVC banner material.' },
        { label: 'Finish', value: 'Hemmed edges with reinforced corners and eyelets.' },
        { label: 'Fixings', value: 'Cable ties or rope through the eyelets, not supplied.' },
      ],
    },
    {
      id: 'mesh',
      title: 'Mesh banner',
      lead: 'For fences, scaffolding and other exposed, windy sites.',
      body: 'A perforated PVC mesh that lets wind pass through the material instead of catching it, so the banner and its fixings stay put where a solid banner would strain or tear.',
      image: '/home/signboards/signboards-loadstreet.jpg',
      specs: [
        { label: 'Size', value: 'Any size up to our maximum roll width, banners over that are seamed.' },
        { label: 'Material', value: 'Perforated PVC mesh, printed one side.' },
        { label: 'Finish', value: 'Hemmed edges with eyelets set to your spacing.' },
        { label: 'Fixings', value: 'Cable ties recommended for scaffold and fence line fixing.' },
      ],
    },
    {
      id: 'double-sided',
      title: 'Double-sided',
      lead: 'Readable from both directions, ideal where a banner hangs free.',
      body: 'Two banners laminated back to back so your artwork reads correctly from either side, used where a banner hangs across a walkway, gateway or between posts rather than against a solid surface.',
      image: '/home/signboards/signboards-outpost.jpg',
      specs: [
        { label: 'Size', value: 'Any size up to our maximum roll width, banners over that are seamed.' },
        { label: 'Material', value: 'Two printed PVC banners laminated back to back.' },
        { label: 'Finish', value: 'Hemmed edges with eyelets set to your spacing.' },
        { label: 'Fixings', value: 'Rope or cable ties through the eyelets, not supplied.' },
      ],
    },
  ],

  customisation: [
    {
      title: 'Full-colour print',
      text: 'Your artwork printed edge to edge on our in-house wide-format printer. Photos, gradients and fine detail all come out sharp.',
    },
    {
      title: 'Eyelets and finishing',
      text: 'Every banner is hemmed and eyeletted by hand to your spacing before it leaves us, so it goes straight up on site.',
    },
  ],

  gallery: [
    '/home/signboards/signboards-banners.jpg',
    '/home/signboards/signboards-walkers.jpg',
    '/home/signboards/signboards-loadstreet.jpg',
    '/home/signboards/signboards-kemp.jpg',
    '/home/signboards/signboards-ijr.jpg',
    '/home/signboards/signboards-blackheart.jpg',
    '/home/signboards/signboards-mayfield.jpg',
  ],

  info: [
    {
      title: 'Professional installation',
      text: 'Our insured install team can fit banners to fences, scaffolding and site hoarding across the Midlands.',
      bullets: ['Fully insured', 'Cable ties and fixings included on installed jobs'],
    },
    {
      title: 'Supply only',
      text: 'Every banner is finished with eyelets ready to fix yourself, no extra tools needed.',
      bullets: ['Collect from Kidderminster', 'Delivery from £10'],
    },
    {
      title: 'Turnaround',
      text: 'Production is usually 5 to 10 working days from approval. Need it sooner? Talk to us and we will do our best.',
    },
  ],

  faqs: [
    {
      q: 'Do you supply the rope or cable ties?',
      a: 'Eyelets are included on every banner, but rope and cable ties are not supplied unless we are installing the banner for you.',
    },
    {
      q: 'What is a mesh banner for?',
      a: 'Mesh is perforated so wind passes through it rather than catching the banner. It is the right choice for fence lines, scaffolding and any exposed or windy site.',
    },
    {
      q: 'Can you match a banner to a long fence or hoarding run?',
      a: 'Yes, longer runs are seamed from multiple widths so the finished banner still hangs and prints as one continuous design.',
    },
  ],

  cta: {
    title: 'Get your free banner quote and mockup.',
    text: 'Tell us the size and where it is going and we will recommend the right material and finish.',
    image: '/home/signboards/signboard-installation.jpg',
  },
};

export default pvcBanners;
